/** Helpers for sync_upload campaign deadlines and auto-link requirements. */

export const SYNC_UPLOAD_APPROVAL_LEAD_DAYS = 3;

export function isSyncUploadCampaign(
    campaign:
        | {
              campaign_type?: string | null;
          }
        | null
        | undefined,
): boolean {
    return campaign?.campaign_type === 'sync_upload';
}

/** Content must be created & brand-approved this many days before sync publish. */
export function getSyncUploadApprovalDeadline(
    syncPublishDate: string | Date | null | undefined,
    leadDays: number = SYNC_UPLOAD_APPROVAL_LEAD_DAYS,
): Date | null {
    if (!syncPublishDate) return null;
    const d = new Date(syncPublishDate);
    if (Number.isNaN(d.getTime())) return null;
    const out = new Date(d);
    out.setDate(out.getDate() - leadDays);
    return out;
}

export function formatSyncUploadDisplayDate(value: string | Date | null | undefined): string {
    if (!value) return '';
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    });
}

/** Sync upload campaigns require OAuth-connected (auto-linked) social accounts. */
export function syncUploadRequiresAutoLink(
    campaign:
        | {
              campaign_type?: string | null;
          }
        | null
        | undefined,
): boolean {
    return isSyncUploadCampaign(campaign);
}

export const SYNC_OAUTH_PLATFORM_IDS = ['instagram', 'tiktok', 'youtube'] as const;
export type SyncOauthPlatformId = (typeof SYNC_OAUTH_PLATFORM_IDS)[number];

export function isSyncOauthPlatformId(value: string): value is SyncOauthPlatformId {
    return (SYNC_OAUTH_PLATFORM_IDS as readonly string[]).includes(value.toLowerCase());
}

export function formatOauthPlatformLabel(id: string): string {
    const v = id.toLowerCase();
    if (v === 'instagram') return 'Instagram';
    if (v === 'tiktok') return 'TikTok';
    if (v === 'youtube') return 'YouTube';
    return id.charAt(0).toUpperCase() + id.slice(1);
}

/** "YouTube" / "Instagram and YouTube" / "Instagram, TikTok, and YouTube" */
export function formatOauthPlatformList(ids: readonly string[]): string {
    const labels = ids.map(formatOauthPlatformLabel);
    if (labels.length === 0) return '';
    if (labels.length === 1) return labels[0];
    if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
    return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
}

/**
 * Platforms that must be OAuth auto-linked for this campaign.
 * Prefers premium required/missing lists; otherwise campaign platforms for sync upload.
 */
export function resolveRequiredOauthPlatforms(
    campaign:
        | {
              campaign_type?: string | null;
              platforms?: string[] | null;
              premium_access?: {
                  required_platforms?: string[] | null;
                  missing_connected_platforms?: string[] | null;
              } | null;
          }
        | null
        | undefined,
): SyncOauthPlatformId[] {
    if (!campaign) return [];

    const normalize = (list?: string[] | null): SyncOauthPlatformId[] => {
        if (!list?.length) return [];
        const out: SyncOauthPlatformId[] = [];
        for (const raw of list) {
            const id = String(raw).trim().toLowerCase();
            if (isSyncOauthPlatformId(id) && !out.includes(id)) out.push(id);
        }
        return out;
    };

    const premiumMissing = normalize(campaign.premium_access?.missing_connected_platforms);
    const premiumRequired = normalize(campaign.premium_access?.required_platforms);
    const campaignPlatforms = normalize(campaign.platforms);

    if (syncUploadRequiresAutoLink(campaign)) {
        return campaignPlatforms.length ? campaignPlatforms : (['instagram'] as SyncOauthPlatformId[]);
    }
    if (premiumMissing.length) return premiumMissing;
    if (premiumRequired.length) return premiumRequired;
    return [];
}
